import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Layout from './Layout';
import Meta from './Meta';
import Container from './Container';
import Breadcrumb from './Breadcrumb';
import Intro from './Intro';
import Dropzone from './Dropzone';
import Features from './Features';
import Faq from './Faq';
import PdfViewer from './PdfViewer';
import FileTable from './FileTable';
import ActionsGrid from './ActionsGrid';
import ErrorModal from './ErrorModal';
import useUpload from '../utils/useUpload';
import usePdfAccept from '../utils/usePdfAccept';
import { saveAs } from 'file-saver';
import FileIcon from "../components/FileIcon";
import IconDownload from "../public/images/download.svg";
import { sendEvent } from '../utils/analytics';
import filesize from "filesize";
import useInterruptRouting from '../utils/useInterruptRouting';
import HowTo from "./HowTo";
import { Result } from './Result';

const PdfEditPage = ({
  meta,
  breadcrumb,
  intro,
  dropzone,
  features,
  faq,
  pdfViewer,
  fileTable,
  actionsGrid,
  onSubmit,
  submitError,
  howTo,
}) => {
  const [{ step, files }, setState] = useState({
    step: 1,
    files: [],
  });
  const [isStepReset, setStepReset] = useInterruptRouting(step);

  const handleGoToStep2 = useCallback((files) => {
    setStepReset(false);
    setState((state) => ({
      ...state,
      step: 2,
      files,
    }));
  }, [setStepReset]);

  useEffect(() => {
    if (fileTable) {
      setState((state) => ({
        ...state,
        step: 3,
      }));
    }
  }, [fileTable]);

  useEffect(() => {
    window.scrollTo(0, 0);
    isStepReset && setState({
      step: 1,
      files: [],
    });
  }, [step, isStepReset]);

  let content;

  if (step === 1) {
    content = (
      <Step1
        breadcrumb={breadcrumb}
        intro={intro}
        dropzone={dropzone}
        features={features}
        howTo={howTo}
        faq={faq}
        onNextStep={handleGoToStep2}
      />
    );
  } else if (step === 2) {
    content = (
      <Step2
        pdfViewer={pdfViewer}
        dropzone={dropzone}
        files={files}
        onSubmit={onSubmit}
        submitError={submitError}
      />
    );
  } else if (step === 3) {
    content = (
      <Step3
        fileTable={fileTable}
        actionsGrid={actionsGrid}
        features={features}
        howTo={howTo}
        faq={faq}
      />
    );
  }

  return (
    <Layout className="page-edit">
      <Meta {...meta} />
      {content}
    </Layout>
  );
};

const Step1 = ({ breadcrumb, intro, dropzone, features, howTo, faq, onNextStep }) => {
  const { t } = useTranslation(['common', 'convert-and-edit']);
  const { basePath } = useRouter();
  const [files, setFiles] = useState([]);
  const { processing, fileWithError, handleAccepted, errorModal } = usePdfAccept(files, setFiles);

  useEffect(() => {
    if (files.length && !fileWithError) {
      onNextStep?.(files);
    }
  }, [files, fileWithError, onNextStep]);

  return (
    <>
      <Container className="my-xs-1 my-md-3">
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            {t('common:nav.index')}
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            {breadcrumb}
          </Breadcrumb.Item>
        </Breadcrumb>
      </Container>
      <Intro
        icon={<img src={`${basePath}/images/intro-2.svg`} width="214" alt="" />}
        {...intro}
      />
      <Container className="my-xs-3 my-sm-4 my-md-5 my-lg-6">
        <Dropzone
          type="pdf"
          disabled={processing || !!fileWithError}
          onAccepted={handleAccepted}
          {...dropzone}
        />
      </Container>
      <HowTo {...howTo} />
      <Features
        style={{ backgroundImage: `url(${basePath}/images/features-2.svg)` }}
        {...features}
      />
      <Faq {...faq} />
      {errorModal}
    </>
  );
};

const Step2 = ({ pdfViewer, dropzone, files: initialFiles, submitError, onSubmit }) => {
  const { t } = useTranslation(['common', 'convert-and-edit']);
  const [files, setFiles] = useState(initialFiles);
  const [showUploadError, setShowUploadError] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const pdfViewerRef = useRef(null);
  const autoSubmit = useRef(false);
  const autoSubmitTimer = useRef(null);

  const {
    processing: processingDropzone,
    fileWithError,
    handleAccepted,
    errorModal: dropzoneErrorModal,
  } = usePdfAccept(files, setFiles);

  const { upload, uploading, error: uploadError } = useUpload();
  const hasFileWithError = !!fileWithError;
  const hasUploadError = !!uploadError;

  const handleUploadErrorModalHide = useCallback(() => {
    setShowUploadError(false);
  }, []);

  const handleSubmitErrorModalHide = useCallback(() => {
    setShowSubmitError(false);
  }, []);

  const handleSubmit = useCallback((data) => {
    if (hasFileWithError || !onSubmit) {
      return;
    }

    if (hasUploadError) {
      autoSubmit.current = true;

      // trigger upload, try fix error
      return setFiles((files) => files.slice());
    }

    onSubmit(data);
  }, [hasFileWithError, hasUploadError, onSubmit]);

  let errorModal = dropzoneErrorModal;

  if (!errorModal) {
    if (uploadError) {
      errorModal = (
        <ErrorModal
          title={t('convert-and-edit:upload-error-modal.title')}
          text={t('convert-and-edit:upload-error-modal.text')}
          show={showUploadError}
          onHide={handleUploadErrorModalHide}
        />
      );
    } else if (submitError) {
      errorModal = (
        <ErrorModal
          title={t('convert-and-edit:processing-error-modal.title')}
          text={t('convert-and-edit:processing-error-modal.text')}
          show={showSubmitError}
          onHide={handleSubmitErrorModalHide}
        />
      );
    }
  }

  useEffect(() => {
    // timeout hack to submit form
    if (autoSubmit.current && !hasUploadError && files.every((file) => file.hash)) {
      const delayedSubmit = (tryCount = 3) => {
        clearTimeout(autoSubmitTimer.current);

        autoSubmitTimer.current = setTimeout(() => {
          if (tryCount <= 0) {
            return;
          }

          const submitted = pdfViewerRef.current?.submit();

          if (!submitted) {
            delayedSubmit(tryCount - 1);
          }
        }, 100);
      };

      autoSubmit.current = false;
      delayedSubmit();
    }

    return () => {
      clearTimeout(autoSubmitTimer.current);
    };
  }, [hasUploadError, files]);

  useEffect(() => {
    if (hasFileWithError) {
      return;
    }

    const filesToUpload = files
      .filter(({ hash }) => !hash)
      .map(({ file }) => file);

    if (!filesToUpload.length) {
      return;
    }

    upload(filesToUpload, (uploadedFiles) => {
      uploadedFiles = uploadedFiles.map(({ hash }, i) => ({
        file: filesToUpload[i],
        hash,
      }));

      setFiles((files) => {
        return files.map((file) => {
          if (file.hash) {
            return file;
          }

          const uploadedFile = uploadedFiles.find((uploadedFile) => uploadedFile.file === file.file);

          if (uploadedFile) {
            return {
              ...file,
              hash: uploadedFile.hash,
            };
          }

          return file;
        });
      });
    });
  }, [hasFileWithError, files, upload]);

  useEffect(() => {
    if (uploadError) {
      setShowUploadError(true);
    }
  }, [uploadError]);

  useEffect(() => {
    if (submitError) {
      setShowSubmitError(true);
    }
  }, [submitError]);

  const maxFiles = (dropzone?.options?.maxFiles || 1) - files.length;

  return (
    <>
      <PdfViewer
        {...pdfViewer}
        files={files.filter((file) => file.pdfDocument)}
        setFiles={setFiles}
        loading={uploading}
        dropzoneProps={{
          disabled: processingDropzone || hasFileWithError || uploading || maxFiles < 1,
          onAccepted: handleAccepted,
          ...dropzone,
          options: {
            ...dropzone?.options,
            maxFiles,
          },
        }}
        disabled={processingDropzone || hasFileWithError}
        onSubmit={handleSubmit}
        ref={pdfViewerRef}
      />
      {errorModal}
    </>
  );
};

const Step3 = ({ fileTable, actionsGrid, features, howTo, faq }) => {
  const { t } = useTranslation(['common', 'convert-and-edit']);
  const { basePath, route } = useRouter();
  return (
    <>
    <Result t={t} result={fileTable} actions={actionsGrid} route={route}/>
      <HowTo {...howTo}/>
      <Features
        style={{ backgroundImage: `url(${basePath}/images/features-2.svg)` }}
        {...features}
      />
      <Faq {...faq} />
    </>
  );
};

PdfEditPage.propTypes = {
  meta: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
  breadcrumb: PropTypes.string.isRequired,
  intro: PropTypes.shape({
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  dropzone: PropTypes.shape({
    options: PropTypes.object,
  }),
  howTo: PropTypes.shape({
    title: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  features: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  faq: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  pdfViewer: PropTypes.shape({
    title: PropTypes.string.isRequired,
    viewType: PropTypes.string,
  }).isRequired,
  fileTable: PropTypes.shape({
    fileType: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    fileSize: PropTypes.number.isRequired,
    fileSizeCompressed: PropTypes.number,
    fileUrl: PropTypes.string,
    compressable: PropTypes.bool,
  }),
  actionsGrid: PropTypes.object,
  onSubmit: PropTypes.func,
  submitError: PropTypes.object,
};

Step1.propTypes = {
  breadcrumb: PropTypes.string.isRequired,
  intro: PropTypes.shape({
    title: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  dropzone: PropTypes.shape({
    options: PropTypes.object,
  }),
  howTo: PropTypes.shape({
    title: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  features: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  faq: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  onNextStep: PropTypes.func,
};

Step2.propTypes = {
  pdfViewer: PropTypes.shape({
    title: PropTypes.string.isRequired,
    viewType: PropTypes.string,
  }).isRequired,
  dropzone: PropTypes.shape({
    options: PropTypes.object,
  }),
  files: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    file: PropTypes.shape({
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
    }).isRequired,
    pdfDocument: PropTypes.object.isRequired,
    password: PropTypes.string,
    hash: PropTypes.string,
  })).isRequired,
  onSubmit: PropTypes.func,
  submitError: PropTypes.object,
};

Step3.propTypes = {
  fileTable: PropTypes.shape({
    fileType: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    fileSize: PropTypes.number.isRequired,
    fileSizeCompressed: PropTypes.number,
    fileUrl: PropTypes.string,
    compressable: PropTypes.bool,
  }),
  actionsGrid: PropTypes.object,
  howTo: PropTypes.shape({
    title: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  features: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  faq: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

export default PdfEditPage;
